"use client";
// A client component since 25.10.2026: the root 404 (app/not-found.tsx via
// RootNotFound) is part of every page's payload, so it may not read cookies on
// the server; it picks the language in the browser. The eight short copies
// (~3 kB gzipped) are the price.
import Link from "@/components/LocaleLink";
import { ArrowRight, Home } from "lucide-react";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { PageIntro } from "@/components/landing/PageIntro";
import { buttonVariants } from "@/components/ui/Button";

const COPY = {
  de: {
    eyebrow: "Fehler 404",
    title: "Diese Seite haben wir",
    accent: "leider nicht gefunden.",
    lead:
      "Vielleicht ist der Link veraltet oder es gab einen Tippfehler. Hier sind die wichtigsten Anlaufpunkte für eure dänische Hochzeit.",
    ctaHome: "Zur Startseite",
    ctaCheck: "Kostenloser 60-Sekunden-Check",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Ratgeber",
        title: "Wissensdatenbank durchsuchen",
        body: "Artikel zu Dokumenten, Apostille, Standesämtern und Kosten.",
        more: "Zum Ratgeber",
      },
      {
        href: "/preise",
        eyebrow: "Pakete",
        title: "Preise und Leistungen",
        body: "Essential €249,99 · Complete €649,99 inkl. AFL-Gebühr, Festpreis im Vertrag.",
        more: "Zu den Paketen",
      },
    ],
  },
  en: {
    eyebrow: "Error 404",
    title: "We couldn't find",
    accent: "that page.",
    lead:
      "The link may be outdated or there was a typo. Here are the most useful starting points for your Danish wedding.",
    ctaHome: "Back to home",
    ctaCheck: "Free 60-second check",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Blog",
        title: "Browse the knowledge base",
        body: "Articles on documents, apostille, registry offices and costs.",
        more: "Open the blog",
      },
      {
        href: "/preise",
        eyebrow: "Packages",
        title: "Pricing and services",
        body: "Essential €249.99 · Complete €649.99 incl. AFL fee, fixed price in the contract.",
        more: "See the packages",
      },
    ],
  },
  da: {
    eyebrow: "Fejl 404",
    title: "Vi kunne desværre ikke finde",
    accent: "denne side.",
    lead:
      "Linket er måske forældet, eller der var en tastefejl. Her er de vigtigste startpunkter til jeres danske bryllup.",
    ctaHome: "Til forsiden",
    ctaCheck: "Gratis 60-sekunders tjek",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Blog",
        title: "Søg i vidensbasen",
        body: "Artikler om dokumenter, apostille, kommuner og omkostninger.",
        more: "Til bloggen",
      },
      {
        href: "/preise",
        eyebrow: "Pakker",
        title: "Priser og ydelser",
        body: "Essential €249,99 · Complete €649,99 inkl. AFL-gebyr, fast pris i kontrakten.",
        more: "Se pakkerne",
      },
    ],
  },
  ar: {
    eyebrow: "خطأ 404",
    title: "لم نتمكن من العثور",
    accent: "على هذه الصفحة.",
    lead:
      "ربما يكون الرابط قديمًا أو فيه خطأ مطبعي. إليكم أكثر نقاط البداية فائدة لزواجكم في الدنمارك.",
    ctaHome: "العودة إلى الصفحة الرئيسية",
    ctaCheck: "الفحص المجاني خلال 60 ثانية",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "الدليل",
        title: "تصفّحوا قاعدة المعرفة",
        body: "مقالات عن الوثائق والأبوستيل ومكاتب الزواج المدني والتكاليف.",
        more: "افتحوا الدليل",
      },
      {
        href: "/preise",
        eyebrow: "الباقات",
        title: "الأسعار والخدمات",
        body: "Essential بسعر €249.99 · Complete بسعر €649.99 شاملًا رسوم AFL، بسعر ثابت في العقد.",
        more: "اطّلعوا على الباقات",
      },
    ],
  },
  uk: {
    eyebrow: "Помилка 404",
    title: "Нам не вдалося знайти",
    accent: "цю сторінку.",
    lead:
      "Можливо, посилання застаріло або в ньому друкарська помилка. Ось найкорисніші відправні точки для вашого весілля в Данії.",
    ctaHome: "На головну",
    ctaCheck: "Безкоштовна перевірка за 60 секунд",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Блог",
        title: "Перегляньте базу знань",
        body: "Статті про документи, апостиль, ратуші та витрати.",
        more: "Відкрити блог",
      },
      {
        href: "/preise",
        eyebrow: "Пакети",
        title: "Ціни та послуги",
        body: "Essential €249,99 · Complete €649,99 включно зі збором AFL, фіксована ціна в договорі.",
        more: "Переглянути пакети",
      },
    ],
  },
  ru: {
    eyebrow: "Ошибка 404",
    title: "Мы не нашли",
    accent: "эту страницу.",
    lead:
      "Возможно, ссылка устарела или в адресе опечатка. Вот самые полезные разделы для подготовки к свадьбе в Дании.",
    ctaHome: "На главную",
    ctaCheck: "Бесплатная проверка за 60 секунд",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Справочник",
        title: "Изучить базу знаний",
        body: "Статьи о документах, апостиле, органах регистрации брака и расходах.",
        more: "Открыть справочник",
      },
      {
        href: "/preise",
        eyebrow: "Пакеты",
        title: "Цены и услуги",
        body: "Essential €249,99 · Complete €649,99 включая сбор AFL, фиксированная цена в договоре.",
        more: "Смотреть пакеты",
      },
    ],
  },
  vi: {
    eyebrow: "Lỗi 404",
    title: "Chúng tôi không tìm thấy",
    accent: "trang này.",
    lead:
      "Có thể đường link đã cũ hoặc bị gõ sai. Dưới đây là những điểm khởi đầu hữu ích nhất cho đám cưới tại Đan Mạch của các bạn.",
    ctaHome: "Về trang chủ",
    ctaCheck: "Kiểm tra miễn phí trong 60 giây",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Cẩm nang",
        title: "Xem kho kiến thức",
        body: "Bài viết về giấy tờ, Apostille, cơ quan hộ tịch và chi phí.",
        more: "Mở cẩm nang",
      },
      {
        href: "/preise",
        eyebrow: "Các gói",
        title: "Giá và dịch vụ",
        body: "Essential €249,99 · Complete €649,99 đã gồm phí AFL, giá cố định trong hợp đồng.",
        more: "Xem các gói",
      },
    ],
  },
  es: {
    eyebrow: "Error 404",
    title: "Lo sentimos, no hemos encontrado",
    accent: "esta página.",
    lead:
      "Quizás el enlace esté desactualizado o haya un error de escritura. Aquí tienen los puntos de partida más importantes para su boda en Dinamarca.",
    ctaHome: "Ir a la página de inicio",
    ctaCheck: "Comprobación gratuita de 60 segundos",
    cards: [
      {
        href: "/ratgeber",
        eyebrow: "Guía",
        title: "Explorar la base de conocimientos",
        body: "Artículos sobre documentos, apostilla, registros civiles y costos.",
        more: "Ir a la Guía",
      },
      {
        href: "/preise",
        eyebrow: "Paquetes",
        title: "Precios y servicios",
        body: "Essential €249,99 · Complete €649,99 incl. tasa de la AFL, precio fijo en el contrato.",
        more: "Ver los paquetes",
      },
    ],
  },
} as const;

export function NotFoundContent() {
  const { locale } = useTranslation();
  const c = COPY[locale] ?? COPY.de;

  return (
    <>
      {/* The same page intro as every page without a photograph. One red
          action; `data-hero-cta` keeps the phone's sticky check bar down while
          this button is on screen, so the same button never shows twice. */}
      <PageIntro eyebrow={c.eyebrow} title={c.title} accent={c.accent} lead={c.lead}>
        <Link
          href="/eignungs-check"
          data-hero-cta
          className={buttonVariants({ variant: "primary", size: "md" })}
        >
          {c.ctaCheck}
          <ArrowRight
            className="h-4 w-4 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
            strokeWidth={2.2}
            aria-hidden
          />
        </Link>
        <Link href="/" className={buttonVariants({ variant: "secondary", size: "md" })}>
          <Home className="h-4 w-4" strokeWidth={1.8} aria-hidden />
          {c.ctaHome}
        </Link>
      </PageIntro>

      <section className="mx-auto max-w-3xl px-5 pb-16 pt-2 sm:px-8 sm:pb-24 sm:pt-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {c.cards.map((card) => (
            <Link
              key={card.href}
              href={card.href}
              className="group block rounded-lg border border-navy-100 bg-white p-5 shadow-[var(--shadow-soft)] transition hover:-translate-y-px hover:border-navy-200 sm:p-6"
            >
              <span className="eyebrow">{card.eyebrow}</span>
              <span className="mt-2 block font-display text-[1.2rem] font-normal leading-snug text-ink-900">
                {card.title}
              </span>
              <span className="mt-1.5 block text-[14px] leading-relaxed text-navy-600">
                {card.body}
              </span>
              <span className="mt-3 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-coral-700">
                {card.more}
                <ArrowRight
                  className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5"
                  strokeWidth={2}
                  aria-hidden
                />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
